import { useState } from 'react'
import { Alert, App, Button, Modal, Space, Table, Typography, Upload } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PaperClipOutlined, UploadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatDateTime } from '@/shared/lib/format'
import { formatBytes } from '@/shared/lib/format.units'
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  attachmentUrl,
  fetchAttachments,
  uploadAttachment,
  type AttachmentOwner,
  type CashAttachment,
} from './cashdeskApi'

export interface AttachmentTarget {
  owner: AttachmentOwner
  id: string
  title: string
  canUpload: boolean
}

interface CashAttachmentsModalProps {
  target: AttachmentTarget | null
  onClose: () => void
}

export function CashAttachmentsModal({ target, onClose }: CashAttachmentsModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [problem, setProblem] = useState<string | null>(null)
  const [opening, setOpening] = useState<string | null>(null)

  const owner = target?.owner ?? 'transfers'
  const id = target?.id ?? ''
  const listKey = queryKeys.cashdesk.attachments(owner, id)

  const attachments = useQuery({
    queryKey: listKey,
    queryFn: ({ signal }) => fetchAttachments(owner, id, signal),
    enabled: target !== null,
  })

  const upload = useMutation({
    mutationFn: (file: File) => uploadAttachment(owner, id, file),
    onSuccess: () => {
      void message.success(t('common.toastCreated'))
      void queryClient.invalidateQueries({ queryKey: listKey })
      void queryClient.invalidateQueries({ queryKey: owner === 'transfers' ? queryKeys.cashdesk.transfers : queryKeys.cashdesk.entries })
    },
    onError: (error) => setProblem(translateError(error, t)),
  })

  const pick = (file: File) => {
    setProblem(null)
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setProblem(t('cashdesk.attachment.tooLarge'))
      return
    }
    upload.mutate(file)
  }

  const open = async (attachment: CashAttachment) => {
    setProblem(null)
    setOpening(attachment.id)
    try {
      window.open(await attachmentUrl(owner, id, attachment.id), '_blank', 'noopener')
    } catch (error) {
      setProblem(translateError(error, t))
    } finally {
      setOpening(null)
    }
  }

  const close = () => {
    setProblem(null)
    upload.reset()
    onClose()
  }

  const columns: ColumnsType<CashAttachment> = [
    {
      key: 'file',
      title: t('cashdesk.attachment.file'),
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Button
            type="link"
            size="small"
            icon={<PaperClipOutlined />}
            loading={opening === row.id}
            onClick={() => void open(row)}
            style={{ paddingInline: 0 }}
          >
            {row.fileName ?? t('cashdesk.attachment.unnamed')}
          </Button>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatBytes(row.sizeBytes, 1)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      key: 'createdBy',
      title: t('cashdesk.attachment.addedBy'),
      width: 220,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{row.createdBy.name}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatDateTime(row.createdAt)}
          </Typography.Text>
        </Space>
      ),
    },
  ]

  return (
    <Modal
      open={target !== null}
      title={t('cashdesk.attachment.title')}
      onCancel={close}
      footer={<Button onClick={close}>{t('common.close')}</Button>}
      destroyOnHidden
      width={680}
    >
      {target ? <Typography.Paragraph type="secondary">{target.title}</Typography.Paragraph> : null}

      {problem ? (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setProblem(null)}
          style={{ marginBottom: 16 }}
          message={problem}
        />
      ) : null}

      {target?.canUpload ? (
        <Space wrap style={{ marginBottom: 16 }}>
          <Upload
            accept={ATTACHMENT_ACCEPT}
            showUploadList={false}
            disabled={upload.isPending}
            beforeUpload={(file) => {
              pick(file)
              return false
            }}
          >
            <Button icon={<UploadOutlined />} loading={upload.isPending}>
              {t('cashdesk.attachment.upload')}
            </Button>
          </Upload>
          <Typography.Text type="secondary">{t('cashdesk.attachment.limits')}</Typography.Text>
        </Space>
      ) : null}

      {attachments.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(attachments.error, t)} />
      ) : null}
      <Table<CashAttachment>
        columns={columns}
        dataSource={attachments.data ?? []}
        rowKey={(row) => row.id}
        loading={attachments.isPending}
        size="small"
        pagination={false}
        locale={{ emptyText: attachments.isError ? t('common.error') : t('cashdesk.attachment.empty') }}
      />
    </Modal>
  )
}
